/**
 * MESSAGE INFO MAPPERS — boundary UI <-> DTO REST per MessageInfo.
 *
 * Shape backend (rest-openapi.yaml, schema MessageInfo):
 *   int64 messageInfoId/messageId/classPK/audit
 *   string label/labelTranslated/value/className/userName/createDate/modifiedDate
 *   int32 priority
 *
 * Shape input backend (schema MessageInfoInput) — solo campi editabili:
 *   string value, int32 priority
 */

import { pickAuditFields } from '@crm/shared';
import type { ExtractorGroup, MessageInfo, MessageInfoUpdatePayload } from './messageInfoTypes';

export interface MessageInfoDto {
  messageInfoId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  messageId: number;
  label?: string | null;
  labelTranslated?: string | null;
  value?: string | null;
  className?: string | null;
  classPK?: number | null;
  priority?: number | null;
}

/** Schema REST ExtractorGroup: gruppo di MessageInfo per extractor di provenienza. */
export interface ExtractorGroupDto {
  className?: string | null;
  infos?: MessageInfoDto[] | null;
}

export interface MessageInfoInput {
  value?: string;
  priority?: number;
}

/** DTO -> modello UI. Normalizza i campi opzionali a valori safe. */
export function messageInfoFromDto(dto: MessageInfoDto): MessageInfo {
  return {
    messageInfoId: dto.messageInfoId,
    ...pickAuditFields(dto),
    messageId: dto.messageId,
    label: dto.label ?? '',
    labelTranslated: dto.labelTranslated ?? '',
    value: dto.value ?? '',
    className: dto.className ?? '',
    classPK: dto.classPK ?? 0,
    priority: dto.priority ?? 0,
  };
}

/** Modello UI -> DTO. Usato dai mock per simulare la shape backend. */
export function messageInfoToDto(mi: MessageInfo): MessageInfoDto {
  return {
    messageInfoId: mi.messageInfoId,
    ...pickAuditFields(mi),
    messageId: mi.messageId,
    label: mi.label,
    labelTranslated: mi.labelTranslated,
    value: mi.value,
    className: mi.className,
    classPK: mi.classPK,
    priority: mi.priority,
  };
}

/** DTO ExtractorGroup -> modello UI. */
export function extractorGroupFromDto(dto: ExtractorGroupDto): ExtractorGroup {
  return {
    className: dto.className ?? null,
    infos: (dto.infos ?? []).map(messageInfoFromDto),
  };
}

/** Modello UI ExtractorGroup -> DTO. Usato dai mock per simulare la shape backend. */
export function extractorGroupToDto(group: ExtractorGroup): ExtractorGroupDto {
  return {
    className: group.className,
    infos: group.infos.map(messageInfoToDto),
  };
}

/** Payload update UI -> DTO input (PATCH body). */
export function messageInfoUpdateToInput(p: MessageInfoUpdatePayload): MessageInfoInput {
  return {
    value: p.value,
    priority: p.priority,
  };
}

/** DTO input (PATCH body) -> Partial<MessageInfo> UI. Usato dai mock. */
export function messageInfoInputFromDto(input: MessageInfoInput): Partial<MessageInfo> {
  const out: Partial<MessageInfo> = {};
  if (input.value !== undefined) out.value = input.value;
  if (input.priority !== undefined) out.priority = input.priority;
  return out;
}
